import { useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { EmptyState } from "@/components/common";
import { AMBER, ingLine } from "@/lib/meals";
import { RecipeThumb, Warnings } from "@/components/meals/parts";
import RecipeDialog from "@/components/meals/RecipeDialog";

function RecipeView({ hubId, r, canEdit, onEdit, onClose, onDeleted }) {
  const del = async () => {
    if (!window.confirm(`Delete ${r.name}? Planned meals keep its name.`)) return;
    try { await api.delete(`/organizations/${hubId}/meals/recipes/${r.id}`); toast.success("Recipe deleted"); onDeleted(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent bigClose className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="recipe-view">
        <DialogHeader><DialogTitle className="flex items-center gap-3 font-display"><RecipeThumb hubId={hubId} recipe={r} size="h-12 w-12" /> <span data-testid="recipe-view-name">{r.name}</span></DialogTitle></DialogHeader>
        <Warnings items={r.warnings} testid="recipe-view-warnings" />
        <p className="text-xs text-q-faint">Serves {r.servings}{r.tags.length ? ` · ${r.tags.join(" · ")}` : ""}</p>
        <h3 className="mt-2 text-sm font-black text-q-text">Ingredients</h3>
        <ul className="list-disc space-y-1 pl-5 text-sm text-q-muted">{r.ingredients.map((i, n) => <li key={n}>{ingLine(i)}</li>)}</ul>
        {r.steps.length > 0 && <><h3 className="mt-2 text-sm font-black text-q-text">Steps</h3><ol className="list-decimal space-y-1 pl-5 text-sm text-q-muted">{r.steps.map((s, n) => <li key={n}>{s}</li>)}</ol></>}
        {r.source_url && <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center break-all text-sm text-q-muted underline">Source</a>}
        {canEdit && (
          <div className="mt-2 flex gap-2">
            <button className="q-btn q-btn-secondary flex-1" onClick={onEdit} data-testid="recipe-edit-btn"><Pencil className="h-4 w-4" /> Edit</button>
            <button className="q-btn q-btn-ghost flex-1" onClick={del} data-testid="recipe-delete-btn"><Trash2 className="h-4 w-4" /> Delete</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function RecipeBox({ hubId, data, q, setQ, tag, setTag, reload }) {
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState(null);
  return (
    <div data-testid="recipe-box">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label className="relative min-w-0 flex-1 basis-56">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-q-faint" />
          <input className="q-input w-full pl-9" value={q} placeholder="Search recipes" onChange={(e) => setQ(e.target.value)} data-testid="recipe-search-input" />
        </label>
        <select className="q-input w-auto" value={tag} onChange={(e) => setTag(e.target.value)} data-testid="recipe-tag-filter">
          <option value="">All tags</option>
          {data.tags.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {data.can_edit && <button className="q-btn q-btn-primary" onClick={() => setForm({})} data-testid="recipe-add-btn"><Plus className="h-4 w-4" /> Add recipe</button>}
      </div>
      {!data.recipes.length ? (
        <EmptyState title="No recipes yet. Add one or import from a link." rgb={AMBER} testid="recipe-empty" />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {data.recipes.map((r) => (
            <button key={r.id} className="q-card flex min-h-[44px] items-start gap-3 text-left transition-transform hover:-translate-y-0.5" onClick={() => setOpen(r)} data-testid={`recipe-card-${r.id}`}>
              <RecipeThumb hubId={hubId} recipe={r} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-display font-extrabold text-q-text" data-testid="recipe-card-name">{r.name}</span>
                <span className="block text-xs text-q-faint">{r.ingredients.length} ingredients{r.tags.length ? ` · ${r.tags.join(" · ")}` : ""}</span>
                <span className="mt-1 block"><Warnings items={r.warnings} compact testid={`recipe-card-warning-${r.id}`} /></span>
              </span>
            </button>
          ))}
        </div>
      )}
      {open && <RecipeView hubId={hubId} r={open} canEdit={data.can_edit} onClose={() => setOpen(null)} onDeleted={reload} onEdit={() => { setForm({ recipe: open }); setOpen(null); }} />}
      {form && <RecipeDialog hubId={hubId} recipe={form.recipe} tags={data.tags} onClose={() => setForm(null)} onSaved={reload} />}
    </div>
  );
}
